import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {DjPsycho} from "../DjPsycho";
import {TimelineLite} from "gsap";
import {DjPsychoGameAssets} from "../DjPsychoGameAssets";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {ParticleController} from "../components/particles/ParticleController";
import {ParticleSettings} from "../components/particles/ParticleSettings";
import {DjPsychoSound} from "../sound/DjPsychoSound";

export class DjPsychoWinCapView extends PIXI.Container implements StageLayer {
    private _countUp: BasicBitmapCountUp;
    private _winSpine: PIXI.spine.Spine;
    private _maxWin: PIXI.spine.Spine;
    private _redBg: PIXI.Graphics;
    private _attentionTl: TimelineLite;
    private _wave: TimelineSprite;
    private _fxSprite: TimelineSprite;
    private _particleEffectsLoops: ParticleController[] = [];

    constructor() {
        super();
        this.name = "DjPsychoWinCapView";
        this.init();
    }

    public show(callback: Function): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._redBg.clear();
        this._redBg.beginTextureFill(DjPsycho.gradient(DjPsycho.gradientColorLevel[3])).drawRect(0, 0, NolimitApplication.screenBounds.width * 2.5, NolimitApplication.screenBounds.height * 2.5).endFill();

        tl.add([
            () => {
                this._redBg.visible = true;

                this.startParticleEffect();
                this._fxSprite.alpha = this._wave.alpha = 1;

                this._fxSprite.show();
                this._fxSprite.playLoop();
                this._wave.show();
                this._wave.playLoop();

                this._winSpine.visible = true;

                DjPsycho.soundPlayer.playSound(DjPsychoSound.CAP_WIN_COUNT_UP);
                DjPsycho.soundPlayer.fadeMusic({
                    name: "",
                    duration: 100,
                    to: 0
                });
            },
            DjPsycho.createTimelineForAnimation(this._winSpine, 0, 'BW_6_In', false)
        ]);
        tl.add([
            () => {
                this._countUp.alpha = 1;
                this._countUp.visible = true;

                this.startFullParticleEffect();
                this.resetSpineAnimation(this._winSpine);
                this._winSpine.state.setAnimation(0, 'BW_6_Loop', true);
            },
            this._countUp.getCountAnimation(0, (DjPsycho.model.gameData.accumulatedRoundWin), 7.218)
        ]);
        tl.add([
            () => {
                this.stopParticleEffect();
            },
            DjPsycho.createTimelineForAnimation(this._winSpine, 0, 'BW_6_Out', false)
        ]);
        tl.to(this._countUp, {duration: 1, y: 360});
        tl.to([this._countUp, this._fxSprite, this._wave], {duration: 0.3, alpha: 0});

        tl.add([
            () => {
                this._winSpine.visible = false;
                this.resetSpineAnimation(this._winSpine);

                this._countUp.visible = false;
                this._fxSprite.hide();
                this._fxSprite.stopLoop();
                this._wave.hide();
                this._wave.stopLoop();

                this._maxWin.visible = true;
            },
            DjPsycho.createTimelineForAnimation(this._maxWin, 0, 'MW_In', false)
        ]);
        tl.add(() => {
            callback();

            this.resetSpineAnimation(this._maxWin);
            this._maxWin.state.setAnimation(0, 'MW_Loop', true);

            DjPsycho.soundPlayer.playSound(DjPsychoSound.CAP_WIN_MUSIC, 1, Number.MAX_VALUE);
        });

        this._attentionTl = tl;
        return tl;
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this._countUp.position.set(360, 580);
            this._wave.position.set(360, 200);
            this._fxSprite.position.set(360, 450);
        } else {
            this._countUp.position.set(360, 740);
            this._wave.position.set(360, -100);
            this._fxSprite.position.set(360, 720);
        }
    }

    public onButtonClick(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add([
            () => {
                this.killTL();

                DjPsycho.soundPlayer.playSound(DjPsychoSound.CAP_WIN_ENDS);
                DjPsycho.soundPlayer.stopSound(DjPsychoSound.CAP_WIN_MUSIC);
                DjPsycho.soundPlayer.fadeMusic({
                    name: "",
                    duration: 1200,
                    to: 1
                });
            },
            DjPsycho.createTimelineForAnimation(this._maxWin, 0, 'MW_Out', false)
        ]);
        tl.add(() => {
            this.onResize();
            this._redBg.visible = false;

            this._maxWin.visible = false;
            this.resetSpineAnimation(this._maxWin);

            // if coming out of any bonus mode
            if (DjPsycho.model.gameData.mode.indexOf("FREESPIN") > -1) {
                DjPsycho.xBetView.removeButton(true);
                DjPsycho.backgroundElements.setMode();
                DjPsycho.miniCollectors.onExitingBonusGame();
            }
        });
        return tl;
    }

    private killTL(): void {
        if (this._attentionTl && this._attentionTl.isActive()) {
            this._attentionTl.pause();
            this._attentionTl.kill();
        }
    }

    private resetSpineAnimation(spineAnimation: PIXI.spine.Spine): void {
        spineAnimation.autoUpdate = true;
        spineAnimation.updateTransform();
        spineAnimation.state.clearTracks();
        spineAnimation.skeleton.setSlotsToSetupPose();
        spineAnimation.skeleton.setToSetupPose();
    }

    private startParticleEffect(): void {
        this._particleEffectsLoops[1].enableEmit(true);
        this._particleEffectsLoops[3].enableEmit(true);
    }

    private startFullParticleEffect(): void {
        this._particleEffectsLoops[0].enableEmit(true);
        this._particleEffectsLoops[2].enableEmit(true);
    }

    private stopParticleEffect(): void {
        this._particleEffectsLoops.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });
    }

    private init(): void {
        this._redBg = new PIXI.Graphics().beginTextureFill(DjPsycho.gradient(DjPsycho.gradientColorLevel[0])).drawRect(-480, -480, 1680, 1680).endFill();
        this._redBg.position.set(-500, -480);
        this._redBg.visible = false;
        this._redBg.alpha = 0.7;

        this._countUp = new BasicBitmapCountUp({
            fontName: DjPsychoGameAssets.BITMAP_FONT_WIN_COUNT_UP_BW,
            fontSize: 52,
        }, 600, new PIXI.Point(0.5, 0.5));
        this._countUp.visible = false;

        this._winSpine = new PIXI.spine.Spine(DjPsycho.assetLoader.getSpineAsset("bigWinInSpine"));
        this._winSpine.position.set(360);
        this._winSpine.visible = false;

        this._maxWin = new PIXI.spine.Spine(DjPsycho.assetLoader.getSpineAsset("maxWin"));
        this._maxWin.position.set(360);
        this._maxWin.visible = false;

        this._wave = new TimelineSprite(DjPsycho.assetLoader.getTextures("FX_Wave/FX_Wave_"), 15);
        this._wave.anchor.set(0.5);
        this._wave.blendMode = PIXI.BLEND_MODES.ADD;
        this._wave.visible = false;
        this._wave.scale.set(3);

        this._fxSprite = new TimelineSprite(DjPsycho.assetLoader.getTextures("FX_EQ/FX_EQ_"));
        this._fxSprite.anchor.set(0.5);
        this._fxSprite.blendMode = PIXI.BLEND_MODES.ADD;
        this._fxSprite.visible = false;
        this._fxSprite.scale.set(2);

        const container: PIXI.Container = new PIXI.Container();
        this._particleEffectsLoops.push(DjPsycho.particleManager.createAnimation(container, "AlienLoop", "P_Alien", ParticleSettings.coinsConfigs, true));
        this._particleEffectsLoops.push(DjPsycho.particleManager.createAnimation(container, "AlienSplash", "P_Alien", ParticleSettings.coinsConfigsSplash, true));
        this._particleEffectsLoops.push(DjPsycho.particleManager.createAnimation(container, "XtcLoop", "Xtc", ParticleSettings.coinsConfigs, true));
        this._particleEffectsLoops.push(DjPsycho.particleManager.createAnimation(container, "XtcSplash", "Xtc", ParticleSettings.coinsConfigsSplash, true));

        this.addChild(this._redBg, this._wave, this._fxSprite, this._winSpine, this._maxWin, container, this._countUp);

        this.onResize();
    }
}